<template>
  <InputWrapper
    v-slot="{ inputId }"
    :input-id="id"
    :label="t('carriers.equipmentTypes')"
    class="equipment-type-filter w-2"
    optional
  >
    <RygenMultiSelect
      :id="inputId"
      v-model="internalValue.equipmentTypes"
      :options="equipmentTypeOptions"
      :loading="isLoading"
      :placeholder="t('carriers.equipmentTypes')"
      append-to="body"
      display="chip"
      option-label="name"
      option-value="naturalId"
      filter
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { computed, onMounted, type PropType, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { OrderSearchCriteria, ShipmentSearchCriteria } from '@/model';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';

const internalValue = defineModel({
  type: Object as PropType<OrderSearchCriteria | ShipmentSearchCriteria>,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});
const { id } = toRefs(props);
const { t } = useI18n();
const {
  equipmentTypes,
  loadEquipmentTypes,
  usedEquipmentTypes,
  loadUsedEquipmentTypes,
} = useEquipmentTypeStore();
const isElasticsearchEnabled = useFlag('elasticsearch-tms-feat-flag');

const equipmentTypeOptions = computed(() => {
  const configuredEquipmentTypes = equipmentTypes.value.data ?? [];

  if (!isElasticsearchEnabled.value) {
    return configuredEquipmentTypes;
  }

  // It's possible for orders/shipments to have equipment types that aren't
  // configured directly in that client. If any of these exist, we add them on
  // top of the configured values so users can still filter them.
  const configuredNaturalIds = new Set(
    configuredEquipmentTypes.map((equipmentType) => equipmentType.naturalId),
  );
  const additionalEquipmentTypes = (usedEquipmentTypes.value.data ?? []).filter(
    (equipmentType) => !configuredNaturalIds.has(equipmentType.naturalId),
  );

  return [...configuredEquipmentTypes, ...additionalEquipmentTypes].sort(
    (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
  );
});

const isLoading = computed(
  () =>
    equipmentTypes.value.loading ||
    (isElasticsearchEnabled.value && usedEquipmentTypes.value.loading),
);

watch(
  isElasticsearchEnabled,
  async (enabled) => {
    if (enabled) {
      await loadUsedEquipmentTypes();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await loadEquipmentTypes();
});
</script>
